<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import {
    OrganisationsService,
    type CustomerOrganisation as Organisation,
    type PatchedCustomerOrganisationRequest
  } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'

  const org = ref<Organisation>()

  onMounted(async () => {
    const response = await OrganisationsService.organisationsRetrieve({
      uuid: 'current'
    })
    org.value = response as Organisation
  })

  const handleSave = async (values: Partial<Organisation>) => {
    await OrganisationsService.organisationsPartialUpdate({
      uuid: 'current',
      requestBody: values as PatchedCustomerOrganisationRequest
    })
  }

  const { t } = useTranslation({
    en: {
      title: 'Email Delivery Tracking',
      description:
        'Configure which email delivery events you want to track. This helps you understand if your emails are being delivered, opened, and clicked.',
      trackDelivery: 'Track email delivery events',
      trackDeliveryDescription:
        'Track when emails are successfully delivered, bounced, or marked as spam',
      trackOpenedClick: 'Track email open and click events',
      trackOpenedClickDescription:
        'Track when recipients open your emails and click on links (requires delivery tracking to be enabled)',
      trackOpenedClickDescriptionHelp:
        'Note: Open tracking is generally accurate but may be affected by email client/settings.'
    },
    de: {
      title: 'E-Mail-Versandstatus',
      description:
        'Hier kann festgelegt werden, welche E-Mail-Versandereignisse verfolgt werden. Dadurch lässt sich nachvollziehen, ob E-Mails erfolgreich zugestellt, geöffnet oder Links darin angeklickt wurden.',
      trackDelivery: 'Zustellung',
      trackDeliveryDescription:
        'Protokolliert, ob E-Mails zugestellt wurden oder als unzustellbar zurückkamen.',
      trackOpenedClick: 'Öffnungen & Klicks',
      trackOpenedClickDescription: 'Erfasst Öffnungen und Linkklicks.',
      trackOpenedClickDescriptionHelp:
        'Hinweis: Der Öffnungsstatus ist in der Praxis meist zutreffend, kann jedoch je nach E-Mail-Client/Einstellungen gelegentlich ungenau sein.'
    },
    fr: {
      title: 'Suivi de l’envoi des e-mails',
      description:
        'Choisissez les événements d’envoi d’e-mails à suivre. Cela permet de savoir si vos e-mails sont bien livrés, ouverts et si les liens sont cliqués.',
      trackDelivery: 'Livraison',
      trackDeliveryDescription:
        'Suit les e-mails livrés avec succès, retournés ou marqués comme indésirables.',
      trackOpenedClick: 'Ouvertures & clics',
      trackOpenedClickDescription:
        'Suit quand les destinataires ouvrent vos e-mails et cliquent sur les liens (nécessite le suivi de livraison).',
      trackOpenedClickDescriptionHelp:
        'Remarque : le suivi des ouvertures est généralement fiable mais peut varier selon le client de messagerie et ses réglages.'
    }
  })
</script>

<template>
  <SettingsPage>
    <AutoSavingForm
      v-if="org"
      class=""
      @save="handleSave"
    >
      <div class="mb-3 block font-medium">{{ t('title') }}</div>
      <p class="mb-4 text-sm text-gray-600">
        {{ t('description') }}
      </p>

      <div class="space-y-4">
        <div>
          <CheckboxField
            v-model="org.track_email_delivery_events"
            name="track_email_delivery_events"
            :label="t('trackDelivery')"
          />
          <p class="text-sm text-gray-500">
            {{ t('trackDeliveryDescription') }}
          </p>
        </div>
        <div v-if="org.track_email_delivery_events">
          <CheckboxField
            v-model="org.track_opened_click_email_delivery_events"
            name="track_opened_click_email_delivery_events"
            :label="t('trackOpenedClick')"
          />
          <p class="text-sm text-gray-500">
            {{ t('trackOpenedClickDescription') }}
            <span class="italic">{{ t('trackOpenedClickDescriptionHelp') }}</span>
          </p>
        </div>
      </div>
    </AutoSavingForm>
  </SettingsPage>
</template>
